/**
 * Staff calls: airport staff calling each other inside the platform, one to
 * one or as a group, by voice or by video — and the administrator's rule for
 * who may call whom.
 *
 * The media is WebRTC between the browsers and phones themselves (a mesh: one
 * connection per pair of people). The platform carries only the signalling
 * and the record, through `/v1/calls`, and it decides who may call whom
 * (api/slt/calls). These are its shapes under the web app's names, and the
 * same decision written once more here, because the sample has no platform
 * to ask and the policy screen explains an answer before it is saved.
 *
 * The decision, in the order the platform takes it:
 *
 *  1. Nobody calls themselves.
 *  2. A disabled sign-in cannot call or be called, and a person without the
 *     calls permission is not on staff calls at all.
 *  3. Every exception whose `from` matches the caller and whose `to` matches
 *     the callee. A single deny refuses — deny beats allow, because an
 *     administrator who wrote "the contractor may not call the CEO" did not
 *     mean "unless some broader rule says they may". Otherwise an allow
 *     admits.
 *  4. Otherwise the level matrix: may a caller at this level call somebody at
 *     that one.
 *
 * The DEFAULT is open — everyone may call everyone, by video, in groups of up
 * to six. An airport's internal line is open until somebody narrows it, and a
 * policy that starts closed makes the first call of a new deployment a
 * support ticket.
 */

export type CallMedia = "audio" | "video";
export type Level = "operations" | "engineering" | "executive" | "master";
export const LEVELS: readonly Level[] = ["operations", "engineering", "executive", "master"];
export const LEVEL_LABEL: Record<Level, string> = {
  operations: "Operations",
  engineering: "Engineering",
  executive: "Executive",
  master: "Master",
};

/** A mesh carries this many people before every browser is sending to too many others. */
export const MESH_CEILING = 8;

export const MEDIA_LABEL: Record<CallMedia, string> = { audio: "Voice", video: "Video" };

/* ── people ───────────────────────────────────────────────────────────── */

export interface CallPhone {
  label: string;
  value: string;
  kind?: string;
}

/** Somebody on staff calls, as the directory shows them to the person looking. */
export interface CallPerson {
  principalId: string;
  name: string;
  level: Level;
  roles: string[];
  jobTitle?: string;
  department?: string;
  employeeId?: string;
  hasPhoto: boolean;
  online: boolean;
  device?: "web" | "phone";
  lastSeenAt?: string;
  /** what the policy says about the person looking calling this one */
  canCall: boolean;
  canVideo: boolean;
  reason?: string;
  phones: CallPhone[];
  /** on Do not disturb now, as far as I am concerned — false when their allow-list holds me */
  dnd?: boolean;
  /** when their Do not disturb ends; absent with `dnd` means until they turn it off */
  dndUntil?: string;
}

/** A directory contact with no sign-in: reached by telephone, not in the app. */
export interface PhoneOnlyContact {
  personId: string;
  name: string;
  org?: string;
  role?: string;
  phones: CallPhone[];
}

/** What the policy says about the person looking, sent with the directory. */
export interface PolicySummary {
  isDefault: boolean;
  myLevel: Level;
  canVideo: boolean;
  canGroup: boolean;
  groupMax: number;
  ringSeconds: number;
  /** my level may ring through somebody's Do not disturb with an urgent call */
  canOverrideDnd?: boolean;
}

export interface CallDirectory {
  items: CallPerson[];
  contacts: PhoneOnlyContact[];
  summary?: PolicySummary;
  /** the sample's people, not anybody's colleagues — the screen says so */
  sample?: boolean;
}

/* ── calls ────────────────────────────────────────────────────────────── */

export type CallState = "ringing" | "active" | "ended";
export type EndReason = "completed" | "missed" | "declined" | "cancelled" | "dropped";
export type ParticipantState =
  | "ringing"
  | "joined"
  | "declined"
  | "missed"
  | "left"
  | "dropped"
  /** in the lobby, asking to be let in */
  | "waiting"
  /** put out by a host */
  | "removed";

export type ParticipantRole = "host" | "cohost" | "member";

/** Who may come into a group call without being rung. */
export type JoinMode = "invited" | "approval" | "open";

export interface JoinRule {
  mode: JoinMode;
  /** let in directly, whatever the mode: people, roles, levels — the policy's matcher */
  allow: PolicyEnd[];
}

export const JOIN_MODE_LABEL: Record<JoinMode, string> = {
  invited: "Invited only",
  approval: "Ask to join",
  open: "Open",
};

export interface Participant {
  principalId: string;
  name: string;
  level?: Level;
  state: ParticipantState;
  audioOn: boolean;
  videoOn: boolean;
  hasPhoto: boolean;
  employeeId?: string;
  invitedBy?: string;
  joinedAt?: string;
  role?: ParticipantRole;
  handRaised?: boolean;
  handAt?: string;
  /** showing their screen: the big tile is theirs */
  sharing?: boolean;
  /**
   * The sample's stand-in for a colleague. Nobody is on the other end: no
   * connection is made to them, and their tile says so.
   */
  simulated?: boolean;
}

export interface Call {
  id: string;
  kind: "direct" | "group";
  media: CallMedia;
  title?: string;
  state: CallState;
  startedBy: string;
  startedByName?: string;
  createdAt: string;
  answeredAt?: string;
  endedAt?: string;
  endReason?: EndReason;
  me: {
    state: ParticipantState;
    audioOn: boolean;
    videoOn: boolean;
    principalId?: string;
    role?: ParticipantRole;
    handRaised?: boolean;
    sharing?: boolean;
  };
  participants: Participant[];
  /** the host's principal (v2); absent on a v1 platform, where the starter leads */
  host?: string;
  join?: JoinRule;
  locked?: boolean;
  urgent?: boolean;
  meetingId?: string;
  /** a call on the sample: nobody is on this line */
  sample?: boolean;
}

export type SignalKind = "offer" | "answer" | "ice" | "hangup" | "control";

/** What a host did to me, or what the platform did — sent by the platform only. */
export interface ControlPayload {
  action: "mute" | "removed" | "admitted" | "denied" | "host" | "share_stopped";
  by?: string;
  to?: string;
}

export interface Signal {
  id: number;
  callId: string;
  from: string;
  kind: SignalKind;
  payload: unknown;
  createdAt: string;
}

export interface CallPoll {
  now: string;
  incoming: Call[];
  calls: Call[];
  signals: Signal[];
  lastSignal: number;
  /** the caller's own principal, when the platform says it */
  me?: string;
  /** my own Do not disturb (v2) */
  dnd?: DndState;
  /** chat lines on my call after `after_msg` (v2; absent from a v1 platform) */
  messages?: CallMessage[];
  lastMessage?: number;
  /** my meetings that are live or start within fifteen minutes (v2) */
  meetings?: Meeting[];
  /** calls whose lobby I am waiting in (v2) */
  waiting?: Call[];
}

/* ── v2: do not disturb, chat, meetings ──────────────────────────────── */

export interface DndState {
  on: boolean;
  until?: string;
  forever: boolean;
  allow: { principalId: string; name: string }[];
}

export const DND_OFF: DndState = { on: false, forever: false, allow: [] };

export interface CallMessage {
  id: number;
  callId: string;
  from: string;
  name: string;
  text: string;
  createdAt: string;
}

export interface MeetingInvitee {
  principalId: string;
  name: string;
  level?: Level;
  hasPhoto: boolean;
  employeeId?: string;
}

export type MeetingState = "scheduled" | "live" | "ended" | "cancelled";

export interface Meeting {
  id: string;
  title: string;
  agenda?: string;
  startsAt: string;
  endsAt: string;
  durationMin: number;
  organiser: string;
  organiserName?: string;
  invitees: MeetingInvitee[];
  join: JoinRule;
  media: CallMedia;
  state: MeetingState;
  callId?: string;
  canStart: boolean;
  canEdit: boolean;
  sample?: boolean;
}

/** What a person fills in to schedule a meeting. */
export interface MeetingDraft {
  title: string;
  agenda: string;
  startsAt: string;
  durationMin: number;
  invitees: string[];
  join: JoinRule;
  media: CallMedia;
}

/** A group call going on now that I could come into: straight in, or by asking. */
export type LiveCall = Call & { can: "join" | "ask" };

export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

export interface IceConfig {
  iceServers: IceServer[];
  /** a TURN relay is configured — without one, calls across networks may not connect */
  relay: boolean;
}

/* ── the policy ───────────────────────────────────────────────────────── */

export type EndType = "principal" | "role" | "level" | "anyone";

export interface PolicyEnd {
  type: EndType;
  id: string | null;
}

export interface PolicyException {
  id: string;
  from: PolicyEnd;
  to: PolicyEnd;
  effect: "allow" | "deny";
  note: string;
}

export interface CallPolicy {
  /** caller level → callee level → may call */
  matrix: Record<Level, Record<Level, boolean>>;
  /** may use video */
  video: Record<Level, boolean>;
  /** may start or extend a group call */
  groupStart: Record<Level, boolean>;
  groupMax: number;
  ringSeconds: number;
  exceptions: PolicyException[];
  /** may ring through Do not disturb with an urgent call — closed for every level by default */
  dndOverride: Record<Level, boolean>;
}

export interface PolicyRole {
  key: string;
  name: string;
  level: Level;
}

export interface PolicyView {
  policy: CallPolicy;
  isDefault: boolean;
  levels: Level[];
  roles: PolicyRole[];
  /** when it was last saved, and by whom; absent while the built-in default stands (build 5) */
  updatedAt?: string;
  updatedBy?: string;
}

const every = <T>(v: T): Record<Level, T> =>
  ({ operations: v, engineering: v, executive: v, master: v }) as Record<Level, T>;

export function defaultPolicy(): CallPolicy {
  return {
    matrix: {
      operations: every(true),
      engineering: every(true),
      executive: every(true),
      master: every(true),
    },
    video: every(true),
    groupStart: every(true),
    groupMax: 6,
    ringSeconds: 45,
    exceptions: [],
    dndOverride: every(false),
  };
}

/** One side of a call as the decision needs it. */
export interface Party {
  principalId: string;
  level: Level;
  roles: string[];
  disabled?: boolean;
  /** holds calls:use; absent means yes */
  onCalls?: boolean;
}

export type DecisionRule = "matrix" | "exception" | "self" | "disabled" | "no_calls";

export interface Decision {
  allowed: boolean;
  video: boolean;
  rule: DecisionRule;
  /** the answer in English, filled — what the platform would say */
  reason: string;
  /** the same answer as a template and its values, for translation where it is shown */
  template: string;
  vars: Record<string, string>;
}

export function endMatches(end: PolicyEnd, p: Party): boolean {
  switch (end.type) {
    case "anyone":
      return true;
    case "principal":
      return end.id === p.principalId;
    case "role":
      return end.id != null && p.roles.includes(end.id);
    case "level":
      return end.id === p.level;
  }
}

function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (w, k: string) => vars[k] ?? w);
}

function answer(
  allowed: boolean,
  video: boolean,
  rule: DecisionRule,
  template: string,
  vars: Record<string, string> = {},
): Decision {
  return { allowed, video, rule, template, vars, reason: fill(template, vars) };
}

/**
 * May `caller` call `callee` under `policy`. `video` says whether the caller's
 * level may use video at all — a call they may make but not by video is made
 * as a voice call, and the reply says why.
 */
export function decide(caller: Party, callee: Party, policy: CallPolicy): Decision {
  const video = policy.video[caller.level] ?? false;
  if (caller.principalId === callee.principalId) return answer(false, false, "self", "You cannot call yourself.");
  if (caller.disabled) return answer(false, false, "disabled", "Your sign-in is disabled.");
  if (callee.disabled) return answer(false, false, "disabled", "This person's sign-in is disabled.");
  if (caller.onCalls === false) return answer(false, false, "no_calls", "You are not on staff calls.");
  if (callee.onCalls === false) return answer(false, false, "no_calls", "This person is not on staff calls.");

  const hits = policy.exceptions.filter((x) => endMatches(x.from, caller) && endMatches(x.to, callee));
  const deny = hits.find((x) => x.effect === "deny");
  if (deny) {
    return deny.note.trim()
      ? answer(false, false, "exception", "Refused by an exception: {note}", { note: deny.note.trim() })
      : answer(false, false, "exception", "Refused by an exception set by the administrator.");
  }
  const allow = hits.find((x) => x.effect === "allow");
  if (allow) {
    return allow.note.trim()
      ? answer(true, video, "exception", "Allowed by an exception: {note}", { note: allow.note.trim() })
      : answer(true, video, "exception", "Allowed by an exception set by the administrator.");
  }
  const vars = { from: LEVEL_LABEL[caller.level], to: LEVEL_LABEL[callee.level] };
  return policy.matrix[caller.level]?.[callee.level]
    ? answer(true, video, "matrix", "{from} may call {to}.", vars)
    : answer(false, false, "matrix", "{from} may not call {to}.", vars);
}

/**
 * May `inviter` bring `invitees` into a call that will then hold `size`
 * people. Every invitee must be callable by the inviter; the people already
 * on the call need not be callable by each other — it is a conference, and
 * the person who invited them is the one the policy asks about.
 */
export function decideGroup(
  inviter: Party,
  invitees: Party[],
  size: number,
  policy: CallPolicy,
): { allowed: boolean; reason?: string; who?: string } {
  if (size > policy.groupMax) return { allowed: false, reason: `A call holds at most ${policy.groupMax} people.` };
  if (size > 2 && !policy.groupStart[inviter.level])
    return { allowed: false, reason: `${LEVEL_LABEL[inviter.level]} may not start group calls.` };
  for (const p of invitees) {
    const d = decide(inviter, p, policy);
    if (!d.allowed) return { allowed: false, reason: d.reason, who: p.principalId };
  }
  return { allowed: true };
}

/** What is wrong with a policy, in the words the platform refuses it with; empty when it may be saved. */
export function validatePolicy(p: CallPolicy, roleKeys?: readonly string[]): string[] {
  const out: string[] = [];
  for (const a of LEVELS) {
    if (!p.matrix[a]) out.push(`The matrix has no row for ${a}.`);
    else for (const b of LEVELS) if (typeof p.matrix[a][b] !== "boolean") out.push(`The matrix has no ${a} → ${b}.`);
    if (typeof p.video[a] !== "boolean") out.push(`Video is not set for ${a}.`);
    if (typeof p.groupStart[a] !== "boolean") out.push(`Group calls are not set for ${a}.`);
    if (p.dndOverride && typeof p.dndOverride[a] !== "boolean") out.push(`Do not disturb is not set for ${a}.`);
  }
  for (const k of [
    ...Object.keys(p.matrix),
    ...Object.keys(p.video),
    ...Object.keys(p.groupStart),
    ...Object.keys(p.dndOverride ?? {}),
  ])
    if (!(LEVELS as readonly string[]).includes(k)) out.push(`Unknown level: ${k}.`);
  if (!Number.isInteger(p.groupMax) || p.groupMax < 2 || p.groupMax > MESH_CEILING)
    out.push(`A group holds between 2 and ${MESH_CEILING} people.`);
  if (!Number.isInteger(p.ringSeconds) || p.ringSeconds < 15 || p.ringSeconds > 120)
    out.push("A call rings for between 15 and 120 seconds.");
  const ids = new Set<string>();
  for (const x of p.exceptions) {
    if (ids.has(x.id)) out.push(`Two exceptions share the id ${x.id}.`);
    ids.add(x.id);
    for (const end of [x.from, x.to]) {
      if (end.type === "anyone") continue;
      if (!end.id) out.push("An exception names a person, role or level and leaves it empty.");
      else if (end.type === "level" && !(LEVELS as readonly string[]).includes(end.id))
        out.push(`Unknown level: ${end.id}.`);
      else if (end.type === "role" && roleKeys && !roleKeys.includes(end.id)) out.push(`Unknown role: ${end.id}.`);
    }
    if (x.effect !== "allow" && x.effect !== "deny") out.push("An exception must allow or deny.");
  }
  return out;
}

/** "Operations may call everyone." — a matrix row in a sentence, as template and values. */
export function rowSummary(
  policy: CallPolicy,
  level: Level,
): { template: string; vars: Record<string, string>; levels: Level[] } {
  const may = LEVELS.filter((l) => policy.matrix[level]?.[l]);
  const vars = { from: LEVEL_LABEL[level] };
  if (may.length === LEVELS.length) return { template: "{from} may call everyone.", vars, levels: may };
  if (may.length === 0) return { template: "{from} may call nobody.", vars, levels: may };
  return { template: "{from} may call only:", vars, levels: may };
}

/** Is this policy the open default. */
export function isDefaultPolicy(p: CallPolicy): boolean {
  return JSON.stringify(p) === JSON.stringify(defaultPolicy());
}

/* ── reading a call ───────────────────────────────────────────────────── */

/** Everyone on the call but me. */
export function others(call: Call, me: string | undefined): Participant[] {
  return call.participants.filter((p) => p.principalId !== me);
}

/** "AZ" from "Adil Zaki"; one letter for one word. */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => !/^(al|el|bin|ibn)-?$/i.test(w));
  if (!words.length) return "?";
  const first = words[0].replace(/^(al|el)-/i, "");
  const last = words.length > 1 ? words[words.length - 1].replace(/^(al|el)-/i, "") : "";
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/** 0:42, 12:05, 1:02:09. */
export function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** How a past call reads in my history. */
export type HistoryOutcome = "missed" | "declined" | "unanswered" | "completed" | "cancelled";

export function historyOutcome(call: Call, me: string | undefined): HistoryOutcome {
  const outgoing = call.startedBy === me;
  if (!outgoing && call.me.state === "missed") return "missed";
  if (!outgoing && call.me.state === "declined") return "declined";
  if (outgoing && !call.answeredAt) return call.endReason === "cancelled" ? "cancelled" : "unanswered";
  return "completed";
}

export const OUTCOME_LABEL: Record<HistoryOutcome, string> = {
  missed: "Missed",
  declined: "Declined",
  unanswered: "No answer",
  completed: "Answered",
  cancelled: "Canceled",
};

/** Online now, seen recently, or not seen — the presence line a directory card shows. */
export function presence(
  p: CallPerson,
  now: number,
): { online: boolean; template: string; vars: Record<string, string> } {
  if (p.online)
    return p.device === "phone"
      ? { online: true, template: "Online · phone", vars: {} }
      : { online: true, template: "Online", vars: {} };
  if (!p.lastSeenAt) return { online: false, template: "Not seen yet", vars: {} };
  const mins = Math.max(0, Math.round((now - Date.parse(p.lastSeenAt)) / 60000));
  if (mins < 60) return { online: false, template: "Last seen {n} min ago", vars: { n: String(Math.max(1, mins)) } };
  const hours = Math.round(mins / 60);
  if (hours < 48) return { online: false, template: "Last seen {n} h ago", vars: { n: String(hours) } };
  return { online: false, template: "Last seen {n} days ago", vars: { n: String(Math.round(hours / 24)) } };
}

/**
 * Who I am on a past call. The platform's poll does not name me, and the
 * directory lists everyone but me — so on a call, I am the one it does not list.
 */
export function meOn(c: Call, me: string | undefined, known: Set<string>): string | undefined {
  if (me) return me;
  if (c.me.principalId) return c.me.principalId;
  const unknown = c.participants.filter((p) => !known.has(p.principalId));
  return unknown.length === 1 ? unknown[0].principalId : undefined;
}

/* ── v2: do not disturb ───────────────────────────────────────────────── */

/** Is a Do not disturb on at `now`: "until I turn it off", or until a time still ahead. */
export function dndActive(d: { on?: boolean; until?: string; forever?: boolean } | undefined, now: number): boolean {
  if (!d) return false;
  if (d.forever) return true;
  if (d.until) return Date.parse(d.until) > now;
  return !!d.on;
}

export type DndChoice = "hour" | "tomorrow" | "forever";

export const DND_CHOICE_LABEL: Record<DndChoice, string> = {
  hour: "1 hour",
  tomorrow: "Until tomorrow 07:00",
  forever: "Until I turn it off",
};

/**
 * When a choice ends, as the platform takes it: an ISO time, or "forever".
 * "Until tomorrow" is seven in the morning by the clock of the device — the
 * person's own morning, not the server's.
 */
export function dndUntilFor(choice: DndChoice, now: Date): string | "forever" {
  if (choice === "forever") return "forever";
  if (choice === "hour") return new Date(now.getTime() + 3600_000).toISOString();
  const t = new Date(now);
  t.setDate(t.getDate() + 1);
  t.setHours(7, 0, 0, 0);
  return t.toISOString();
}

/**
 * What happens when `caller` rings somebody on Do not disturb — the platform's
 * rule, written again for the sample and for the confirm a card shows first:
 * on the allow-list it rings; urgent from a level the policy lets through, it
 * rings; urgent from any other level is refused outright; anything else is
 * held — no ring, a missed call for them, a note for the caller.
 */
export function dndOutcome(
  callee: { dnd: { on?: boolean; until?: string; forever?: boolean; allow: string[] } | undefined },
  caller: string,
  urgent: boolean,
  mayOverride: boolean,
  now: number,
): "ring" | "held" | "refused" {
  if (urgent && !mayOverride) return "refused";
  if (!callee.dnd || !dndActive(callee.dnd, now)) return "ring";
  if (callee.dnd.allow.includes(caller)) return "ring";
  if (urgent) return "ring";
  return "held";
}

/* ── v2: hosts, who can join, hands, sharing ─────────────────────────── */

/** My role on a call. On a v1 platform, which names no host, the person who started it leads. */
export function roleOf(call: Call, who: string | undefined): ParticipantRole {
  if (!who) return "member";
  const p = call.participants.find((x) => x.principalId === who);
  if (p?.role) return p.role;
  if (call.host) return call.host === who ? "host" : "member";
  return call.startedBy === who ? "host" : "member";
}

/** Hosts and co-hosts run the call: the lobby, muting, removing, the settings. */
export function moderates(call: Call, who: string | undefined): boolean {
  const r = roleOf(call, who);
  return r === "host" || r === "cohost";
}

/** People asking to be let in, oldest first. */
export function lobby(call: Call): Participant[] {
  return call.participants.filter((p) => p.state === "waiting");
}

/** Raised hands in the order they went up — the order a chair takes them. */
export function handQueue(call: Call): Participant[] {
  return call.participants
    .filter((p) => p.state === "joined" && p.handRaised)
    .sort((a, b) => (a.handAt ?? "").localeCompare(b.handAt ?? "") || a.name.localeCompare(b.name));
}

/** The one person showing their screen, if anybody is. */
export function sharer(call: Call): Participant | undefined {
  return call.participants.find((p) => p.state === "joined" && p.sharing);
}

export type JoinOutcome = { outcome: "joined" } | { outcome: "waiting" } | { outcome: "refused"; reason: string };

/**
 * May somebody come into a call, and how — the platform's `/join` decision,
 * in its order: an ended call, a removed person, a locked call, already in,
 * invited, on the allow-list, open, ask, invited-only. `mayCallHost` is the
 * administrator's policy (may the joiner call the host), which applies to
 * everyone who was not invited.
 */
export function joinDecision(
  call: Call,
  joiner: Party,
  mayCallHost: { allowed: boolean; reason: string },
): JoinOutcome {
  if (call.state === "ended") return { outcome: "refused", reason: "This call has ended." };
  const row = call.participants.find((p) => p.principalId === joiner.principalId);
  if (row?.state === "removed") return { outcome: "refused", reason: "The host removed you from this call" };
  const invited = !!row && row.state !== "waiting";
  if (call.locked && !invited) return { outcome: "refused", reason: "The host has locked this call" };
  if (row?.state === "joined") return { outcome: "joined" };
  if (invited) return { outcome: "joined" };
  if (!mayCallHost.allowed) return { outcome: "refused", reason: mayCallHost.reason };
  const join = call.join ?? { mode: "invited", allow: [] };
  if (join.allow.some((e) => endMatches(e, joiner))) return { outcome: "joined" };
  if (join.mode === "open") return { outcome: "joined" };
  if (join.mode === "approval") return { outcome: "waiting" };
  return { outcome: "refused", reason: "Only invited people can join this call" };
}

/* ── v2: meetings ─────────────────────────────────────────────────────── */

export type ReminderStage = "15" | "5" | "live";

/**
 * Which reminder a meeting is due: fifteen minutes before, five minutes
 * before, and once it is live. A meeting more than its own length past its
 * start, ended or cancelled, is due nothing.
 */
export function reminderStage(m: Meeting, now: number): ReminderStage | null {
  if (m.state === "ended" || m.state === "cancelled") return null;
  if (m.state === "live") return "live";
  const mins = (Date.parse(m.startsAt) - now) / 60000;
  if (mins > 15) return null;
  if (mins < -m.durationMin) return null;
  return mins <= 5 ? "5" : "15";
}

/** Upcoming meetings: live first, then by start. */
export function sortUpcoming(ms: Meeting[]): Meeting[] {
  return [...ms].sort(
    (a, b) => Number(b.state === "live") - Number(a.state === "live") || a.startsAt.localeCompare(b.startsAt),
  );
}

/** What is wrong with a meeting before it is sent; empty when it may be. */
export function validateMeeting(d: MeetingDraft, groupMax: number, now: number): string[] {
  const out: string[] = [];
  if (!d.title.trim()) out.push("Give the meeting a title.");
  if (d.title.trim().length > 160) out.push("A title is at most 160 characters.");
  if (!d.invitees.length) out.push("Invite at least one person.");
  if (d.invitees.length + 1 > groupMax) out.push("Too many people for one call.");
  if (!Number.isFinite(Date.parse(d.startsAt))) out.push("Choose a date and a time.");
  else if (Date.parse(d.startsAt) < now - 5 * 60000) out.push("That time has passed.");
  if (!Number.isInteger(d.durationMin) || d.durationMin < 5 || d.durationMin > 480)
    out.push("A meeting lasts between 5 minutes and 8 hours.");
  return out;
}

/** What a call can be started with beyond who and how (v2). */
export interface CallStartOptions {
  title?: string;
  join?: JoinRule;
  /** ring through Do not disturb — only from a level the policy lets */
  urgent?: boolean;
}

/** The host's settings a call can be changed to mid-call. */
export interface CallSettingsPatch {
  join?: JoinRule;
  locked?: boolean;
  title?: string;
}
